import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Grid, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { EventManagerWrapper } from '../../EventComponents/EventManagerWrapper';
import SegmentedControl from '../../SegmentedControl/SegmentedControl';
import { IPagination } from '../../../hook/usePagination';
import { ContestDetailsTab } from './Tabs/ContestDetailsTab';
import { ContestSubmissionsTab } from './Tabs/ContestSubmissionsTab';
import { ContestAnalyticsTab } from './Tabs/ContestAnalyticsTab';
import { ContestPromotionsTab } from './Tabs/ContestPromotionsTab';
import { ContestAwardsTab } from './Tabs/ContestAwardsTab';
import { IContest, IProject, ISaleObject } from '../../../types/global/helper';
import { ICampaignSales, IContestManageResponse, TSortBy } from '../../../redux/contest/types';
import CustomButton from '../../Button/CustomButton';
import { DialogueSheet } from '../../DialogueSheet/DialogueSheet';
import { useDispatch, useSelector } from 'react-redux';
import { CustomizedSwitches } from '../../Switch/Switch';
import { fundingComplete, publishContest, unPublishContest } from '../../../redux/contest/actions';
import { ContestCoverImage } from '../../ContestComponents/ContestCoverImage';
import { ThemeStyle } from '../../../module/ThemeManager';
import { ContestDiscountTab } from './Tabs/ContestDiscountsTab';
import { isEmpty } from '../../../utils/lodash';
import { ContestJurorsTab } from './Tabs/ContestJurorsTab';
import { managedCampaignSelector } from '../../../pages/Contests/selector';
import { ContestSalesTab } from './Tabs/ContestSalesTab';
import { formatCurrency } from '../../../utils/AppUtils';
import { Spinner } from '../../Loading/Spinner';
import { IRequestUpdateBoxProps, RequestUpdateBox } from '../../RequestUpdateBox/RequestUpdateBox';
import { CampaignFundingModal } from '../../RequestsTab/CampaignFundingModal';
import { ICurrencyInfo } from '../../../redux/checkout/types';
import { CampaignManageModal } from '../../EsContestEngageModal';
import { setmedia } from '../../../redux/project/actions';

interface IContestManageContainerProps extends Omit<IRequestUpdateBoxProps, 'request'> {
    contest: IContest;
    submissions: IContestManageResponse['submissions'];
    awards: IContestManageResponse['award_categories'];
    sales: IContestManageResponse['sales'];
    votes: IContestManageResponse['votes'];
    discounts: IContestManageResponse['discounts'];
    onUpdate: (contest: Partial<IContest>) => void;
    onImageChange: (image: string, rawFile: File) => void;
    loading: boolean;
    error: any;
    onClearAllFilters: () => void;
    onClearError: () => void;
    updatingInProgress?: boolean;
    activeTab: string;
    submissionPagination: IPagination;
    sortOrder: 'asc' | 'desc';
    onSearch: (search: string) => void;
    onSort: (sort: string) => void;
    onSortBy: (sortBy: TSortBy) => void;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onClearStartDate: () => void;
    onClearEndDate: () => void;
    onTabChange: (tab: string) => void;
    onSubmissionRowsChange: (rows: number) => void;
    onSubmissionPageChange: (page: number) => void;
    onEditDetails: (sectionName: string) => void;
    onExportCSV: (type: 'submissions' | 'awards' | 'sales', data: any) => void;
    onClearSearch: () => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onSaveChanges: (payload: Record<string, any>) => void;
    onDeleteCategoryAward?: (index: number, awardId: string) => void;
    salePagination: IPagination;
    onSaleRowsChange: (rows: number) => void;
    onSalePageChange: (page: number) => void;
}

interface StatsBoxProps {
    label: string;
    value: string | number;
    themeStyle: ThemeStyle;
}

const StatsBox: React.FC<StatsBoxProps> = ({ label, value, themeStyle }) => (
    <Box
        sx={{
            backgroundColor: theme[themeStyle].secondaryBg,
            borderRadius: 2,
            padding: 2,
            flex: 1,
        }}>
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_REGULAR,
                fontSize: '1rem',
                color: theme[themeStyle].light_gray_text,
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: '1.75rem',
                color: theme[themeStyle].primary_white,
            }}>
            {value}
        </Typography>
    </Box>
);

export const ContestManageContainer: React.FC<IContestManageContainerProps> = ({
    contest,
    submissions,
    awards,
    sales,
    votes,
    onImageChange,
    loading,
    error,
    onClearError,
    updatingInProgress,
    sortOrder,
    onSearch,
    onStartDateChange,
    onEndDateChange,
    onClearStartDate,
    onClearEndDate,
    onSubmissionRowsChange,
    onSubmissionPageChange,
    onEditDetails,
    onExportCSV,
    onClearSearch,
    onSortOrderChange,
    onSortBy,
    discounts,
    onSaveChanges,
    onDeleteCategoryAward,
    ...props
}) => {
    const { themeStyle, device } = useThemeContext();
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [cannotUnpublishDialogOpen, setCannotUnpublishDialogOpen] = useState(false);
    const [isFundingModalOpen, setIsFundingModalOpen] = useState(false);
    const { managedContest } = useSelector(managedCampaignSelector);
    const [payload, setPayload] = useState<{ saleObject?: ISaleObject; currencyInfo?: ICurrencyInfo }>({});
    const [showCampaignmanager, setShowCampaignManager] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const tabs = useMemo(
        () => [
            { value: 'details', label: 'Details' },
            { value: 'submissions', label: 'Submissions' },
            { value: 'jurors', label: 'Jurors' },
            { value: 'discounts', label: 'Discounts' },
            { value: 'sales', label: 'Sales' },
            { value: 'awards', label: 'Awards' },
            { value: 'analytics', label: 'Analytics' },
            { value: 'promotions', label: 'Promotions' },
        ],
        [],
    );
    const [visibleTab, setVisibleTab] = useState(tabs[0].value);

    const handleVideoClick = (video: IProject) => {
        dispatch(
            setmedia({
                media: video,
                mediaType: 'video',
            }),
        );
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabFromQuery = params.get('tab');
        const defaultTabValue = tabs[0].value;

        const targetTab = tabs.find(t => t.value === tabFromQuery)?.value || defaultTabValue;

        if (targetTab !== visibleTab) {
            setVisibleTab(targetTab);
        }

        if (params.get('tab') !== targetTab) {
            navigate({ search: `?tab=${targetTab}` }, { replace: true });
        }
    }, [location.search, visibleTab, navigate, tabs]);

    const handleSegmentChange = (tabId: string) => {
        if (tabId === null) {
            return;
        }
        navigate({ search: `?tab=${tabId}` }, { replace: true });
        setVisibleTab(tabId);
    };

    const renderSubmissionsTab = () => {
        if (!contest) return null;
        const pagination: IPagination = {
            page: submissions.pagination.currentPage,
            rows: submissions.pagination.pageSize,
            totalCount: submissions.pagination.totalCount,
        };
        return (
            <ContestSubmissionsTab
                submissions={submissions.data}
                pagination={pagination}
                sortOrder={sortOrder}
                onSearchChange={onSearch}
                onStartDateChange={onStartDateChange}
                onEndDateChange={onEndDateChange}
                onSortOrderChange={onSortOrderChange}
                onRowsChange={onSubmissionRowsChange}
                onPageChange={onSubmissionPageChange}
                onClearSearch={onClearSearch}
                onClearStartDate={onClearStartDate}
                onClearEndDate={onClearEndDate}
                onExportCSV={submissions => onExportCSV('submissions', submissions)}
                onSortByChange={onSortBy}
                contestId={contest._id}
                contest={contest}
                awardCategories={awards}
            />
        );
    };

    const handleFundComplete = (sale: ICampaignSales) => {
        // You might want to refresh the contest data here or update the UI
        dispatch(fundingComplete(sale));
        // Handle successful payment
        setIsFundingModalOpen(false);
    };

    if (loading && !contest) {
        return (
            <Container
                disableGutters={device.isMobile}
                sx={{
                    py: 4,
                    overflow: 'hidden',
                    mr: {
                        xs: 0,
                        sm: 4,
                    },
                    p: { xs: 2 },
                    ml: { xs: 0, sm: 10 },
                    width: { xs: '100%', sm: '85%', smMd: '90%', xl: '95%' },
                }}>
                <Spinner />
            </Container>
        );
    }

    if (error) {
        return (
            <Container
                disableGutters={device.isMobile}
                sx={{
                    height: 'auto',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    width: { xs: '100%', sm: '85%', smMd: '90%', xl: '95%' },
                    margin: 10,
                }}>
                <Box
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].table_header,
                        textAlign: 'flex-start',
                        maxWidth: '500px',
                        width: '100%',
                    }}>
                    <Typography
                        color="error"
                        sx={{
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.2rem',
                        }}>
                        Error loading contest details
                    </Typography>
                    <Typography
                        sx={{
                            mb: 3,
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        We couldn't load the contest details at this time. Please try again.
                    </Typography>
                    <CustomButton
                        onClick={onClearError}
                        label="Retry"
                        customStyle={{
                            width: '100%',
                            borderRadius: '10px',
                        }}
                        sx={{
                            '&:hover': {
                                WebkitTextFillColor: theme[themeStyle].primary_orange,
                            },
                        }}
                    />
                </Box>
            </Container>
        );
    }

    const enableFundingAndPublish = contest.request && contest.request.request_status === 'approved';

    // Handle Start Fund
    const handleFund = () => {
        if (enableFundingAndPublish) {
            /** Item here will always contain One item */
            const saleObject: ISaleObject = {
                category: 'funding',
                item: [
                    {
                        campaign_fund: true,
                    },
                ],
                transaction_currency: 'usd',
                linked_event: contest._id,
                linked_event_type: 'Contest',
            };
            const currencyInfo: ICurrencyInfo = {
                transaction_currency: 'usd',
                original_currency: 'usd',
                rate: 1,
            };
            setPayload({
                saleObject,
                currencyInfo,
            });
            setIsFundingModalOpen(true);
        }
    };

    return (
        <Container
            disableGutters
            maxWidth={false}
            sx={{
                height: 'auto',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%',
            }}>
            <EventManagerWrapper
                loading={updatingInProgress}
                header="Manage Campaign"
                withEndIcon
                onIconClick={() => setShowCampaignManager(!showCampaignmanager)}>
                {/* Hero Section with Cover Image */}
                {contest && <ContestCoverImage contest={contest} editMode onImageChange={onImageChange} />}
                {contest.request && contest.request.request_status === 'action requested' && (
                    <RequestUpdateBox request={contest.request} mode={themeStyle} {...props} />
                )}
                <CampaignManageModal
                    open={showCampaignmanager}
                    onClose={() => setShowCampaignManager(!showCampaignmanager)}
                />
                {contest && (
                    <Stack
                        direction={{ xs: 'row' }}
                        justifyContent={{ xs: 'space-between' }}
                        alignItems={{ xs: 'center' }}
                        mb={2}>
                        <CustomButton
                            label={enableFundingAndPublish ? 'Fund Campaign' : 'Pending Approval'}
                            onClick={handleFund}
                            disabled={!enableFundingAndPublish}
                            textStyle={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                            }}
                        />

                        <CustomizedSwitches
                            label={contest.status === 'published' ? 'Published' : 'Draft'}
                            switchProps={{
                                checked: contest.status === 'published',
                                disabled:
                                    (!enableFundingAndPublish && !contest.funded && !contest.approved) ||
                                    (!isEmpty(submissions.data) && contest.status === 'published'),
                                onChange: () => {
                                    if (contest.status === 'published') {
                                        dispatch(unPublishContest({ documentId: contest._id }));
                                    } else {
                                        dispatch(publishContest({ documentId: contest._id }));
                                    }
                                },
                            }}
                        />
                    </Stack>
                )}

                {/* Stats Summary */}
                <Box sx={{ mb: 4, mt: 4, width: '100%' }}>
                    <Grid container spacing={3}>
                        {[
                            {
                                label: 'Total Submissions',
                                value: submissions?.pagination.totalCount || 0,
                            },
                            {
                                label: 'Total Sales',
                                value: `${sales?.total?.formatted_amount || '$0.00'}`,
                            },
                            {
                                label: 'Total Votes',
                                value: votes?.total || 0,
                            },
                            {
                                label: 'Available Funds',
                                value: contest.amount_available
                                    ? `${formatCurrency(contest.amount_available, '$')}`
                                    : `$${0}`,
                            },
                        ].map((stat, index) => (
                            <Grid item xs={12} sm={6} mdSm={6} md={3} key={index}>
                                <StatsBox label={stat.label} value={stat.value} themeStyle={themeStyle} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Segmented Control for Tabs */}
                <Box sx={{ mb: 4 }}>
                    <SegmentedControl
                        initialValue={visibleTab}
                        options={tabs}
                        onChange={handleSegmentChange}
                        fullWidth
                    />
                </Box>

                {/* Tab Content */}
                <Box sx={{ mt: 4 }}>
                    {visibleTab === 'details' && (
                        <ContestDetailsTab
                            contest={contest}
                            onEditDetails={onEditDetails}
                            onSaveChanges={onSaveChanges}
                            onDeleteCategoryAward={onDeleteCategoryAward}
                            onVideoClick={handleVideoClick}
                            disabled={{
                                categories: {
                                    disabled: !isEmpty(submissions.data),
                                    message: `Campaign already has submissions. You cannot change the categories`,
                                },
                                submission_state: {
                                    disabled: !isEmpty(submissions.data),
                                    message: `Campaign already has submissions. You cannot change the submission state`,
                                },
                                preselection_enabled: {
                                    disabled: !isEmpty(submissions.data),
                                    message: `Campaign already has submissions. You cannot change the pre-selection settings`,
                                },
                                refund_policy: {
                                    disabled: !isEmpty(submissions.data),
                                    message: `Campaign already has submissions. You cannot change the refund_policy settings`,
                                },
                            }}
                        />
                    )}
                    {visibleTab === 'submissions' && renderSubmissionsTab()}
                    {visibleTab === 'jurors' && (
                        <ContestJurorsTab
                            jurors={managedContest?.jurors || []}
                            contestId={contest._id}
                            preselectionEnabled={contest.preselection_enabled}
                        />
                    )}
                    {visibleTab === 'discounts' && (
                        <ContestDiscountTab
                            contestId={contest._id}
                            discounts={{
                                data: discounts.discounts,
                                total: discounts.total,
                            }}
                        />
                    )}
                    {visibleTab === 'sales' && (
                        <ContestSalesTab
                            sales={sales}
                            contestId={contest._id}
                            onExportCSV={sales => onExportCSV('sales', sales)}
                        />
                    )}
                    {visibleTab === 'awards' && (
                        <ContestAwardsTab
                            awardCategories={awards}
                            contest={contest}
                            hasSubmissions={!isEmpty(submissions.data)}
                        />
                    )}
                    {visibleTab === 'analytics' && <ContestAnalyticsTab />}
                    {visibleTab === 'promotions' && <ContestPromotionsTab />}
                </Box>

                {/* Funding Modal */}
                <CampaignFundingModal
                    open={isFundingModalOpen}
                    onClose={() => setIsFundingModalOpen(false)}
                    contest={contest}
                    awards={awards}
                    onFundComplete={handleFundComplete}
                    {...payload}
                />

                {/* Publish Confirmation Dialog */}
                <DialogueSheet
                    open={publishDialogOpen}
                    onClose={() => setPublishDialogOpen(false)}
                    onAgreeClicked={() => {
                        if (contest?._id) {
                            dispatch(publishContest({ documentId: contest._id }));
                        }
                        setPublishDialogOpen(false);
                    }}
                    onDisagreeClick={() => setPublishDialogOpen(false)}
                    header="Publish Contest"
                    body={`This contest has submissions. Are you sure you want to publish it?`}
                    positiveText="Publish"
                    negativeText="Cancel"
                    negtiveTextStyle={{
                        color: theme[themeStyle].tag_white,
                    }}
                />
                {/* Cannot Unpublish Dialog */}
                <DialogueSheet
                    open={cannotUnpublishDialogOpen}
                    onClose={() => setCannotUnpublishDialogOpen(false)}
                    onAgreeClicked={() => setCannotUnpublishDialogOpen(false)}
                    onDisagreeClick={() => setCannotUnpublishDialogOpen(false)}
                    header="Cannot Unpublish Contest"
                    body={
                        'You cannot unpublish this contest because it has submissions. To unpublish, you must cancel the contest.'
                    }
                    positiveText="OK"
                    negativeText="Cancel"
                    negtiveTextStyle={{
                        color: theme[themeStyle].primary_red,
                    }}
                />
            </EventManagerWrapper>
        </Container>
    );
};
